import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, marketApi } from '../lib/api';
import { useApp } from '../store';
import type { Panel } from '../store';
import { useTranslation } from 'react-i18next';
import { useWalletChargeClock } from '../hooks/useWalletChargeClock';
import { NotificationsBell } from './NotificationsBell';
import { ResourceHud } from './ResourceHud';

function formatCountdown(ms: number | null, full: string): string {
  if (ms === null) return full;
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function panelBtnClass(active: boolean): string {
  return `shrink-0 rounded-lg border px-1.5 py-1 text-[10px] font-medium backdrop-blur-md transition sm:rounded-2xl sm:px-3 sm:py-2 sm:text-xs ${
    active
      ? 'border-[var(--accent)] bg-[var(--accent)]/15 text-[var(--accent)]'
      : 'border-[var(--line)] bg-[var(--bg-panel)] hover:border-[var(--accent)]'
  }`;
}

const spotClass =
  'relative z-[50] pointer-events-none ring-2 ring-[var(--accent)] shadow-[0_0_24px_rgba(196,163,90,0.35)]';

type NavPanel = Exclude<Panel, 'none'>;

type NavItem = {
  id: NavPanel;
  label: string;
  show: boolean;
  badge?: number;
  /** Petit point rouge (ex. messages chat non lus). */
  dot?: boolean;
};

export function TopBar() {
  const { t } = useTranslation(['panels', 'common']);
  const { user, wallet, zones, panel, togglePanel, setPanel, tutorialHighlight, clearChatPeer } = useApp();
  const remaining = useWalletChargeClock();
  const [allianceRequests, setAllianceRequests] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) {
      setAllianceRequests(0);
      return;
    }
    let active = true;
    const refresh = () => void api.alliances().then((data) => {
      if (active) setAllianceRequests(data.incomingCount);
    }).catch(() => undefined);
    refresh();
    const id = window.setInterval(refresh, 60_000);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [user, panel]);

  const chatUnreadQuery = useQuery({
    queryKey: ['chat-unread', user?.id],
    queryFn: () => api.chatUnread(),
    enabled: !!user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const chatHasUnread = (chatUnreadQuery.data?.unreadCount ?? 0) > 0;

  const suggestionsUnreadQuery = useQuery({
    queryKey: ['suggestions-unread', user?.id],
    queryFn: () => api.suggestionsUnread(),
    enabled: !!user?.isAdmin,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });
  const suggestionsUnread = suggestionsUnreadQuery.data?.unreadCount ?? 0;

  const onuQuery = useQuery({
    queryKey: ['onu-state-nav', user?.id],
    queryFn: () => api.onuState(),
    enabled: !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const onuVoting = !!onuQuery.data?.voting;
  const onuNeedsVote =
    onuVoting &&
    !!onuQuery.data?.voting?.me?.canVote &&
    !onuQuery.data?.voting?.me?.myVote;

  const marketUnreadQuery = useQuery({
    queryKey: ['market-unread', user?.id],
    queryFn: () => marketApi.unread(),
    enabled: !!user && panel !== 'market',
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const marketHasUnread = panel !== 'market' && (marketUnreadQuery.data?.unreadCount ?? 0) > 0;

  // Close mobile menu on outside click / Escape / panel change.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [panel]);

  // Tutorial: open the mobile menu when highlighting nav so buttons stay visible.
  useEffect(() => {
    if (tutorialHighlight === 'nav') setMenuOpen(true);
  }, [tutorialHighlight]);

  const spotCharges = tutorialHighlight === 'charges';
  const spotNav = tutorialHighlight === 'nav';
  const spotlighting = spotCharges || spotNav;

  const navItems: NavItem[] = [
    { id: 'resources', label: t('common:nav.resources'), show: !!user },
    { id: 'technologies', label: t('common:nav.technologies'), show: !!user },
    { id: 'buildings', label: t('common:nav.buildings'), show: !!user },
    { id: 'empire', label: t('common:nav.empire'), show: !!user },
    {
      id: 'alliances',
      label: t('common:nav.alliances'),
      show: !!user,
      badge: allianceRequests > 0 ? allianceRequests : undefined,
    },
    {
      id: 'onu',
      label: t('common:nav.onu'),
      show: !!user,
      dot: onuNeedsVote,
    },
    { id: 'leaderboard', label: t('common:nav.leaderboard'), show: true },
    {
      id: 'market',
      label: t('common:nav.market'),
      show: !!user,
      dot: marketHasUnread,
    },
    {
      id: 'chat',
      label: t('common:nav.chat'),
      show: !!user,
      dot: chatHasUnread,
    },
    {
      id: 'suggestions',
      label: t('common:nav.suggestions'),
      show: !!user?.isAdmin,
      badge: suggestionsUnread > 0 ? suggestionsUnread : undefined,
    },
    { id: 'admin', label: t('common:nav.admin'), show: !!user?.isAdmin },
  ];

  const openAuth = () => {
    if (panel === 'auth') setPanel('none');
    else setPanel('auth');
    setMenuOpen(false);
  };

  const selectNav = (id: NavPanel) => {
    if (id === 'chat') clearChatPeer();
    togglePanel(id);
    setMenuOpen(false);
  };

  const totalBadge =
    allianceRequests +
    (chatHasUnread ? 1 : 0) +
    suggestionsUnread +
    (onuNeedsVote ? 1 : 0) +
    (marketHasUnread ? 1 : 0);
  const menuActive =
    menuOpen ||
    navItems.some((item) => item.show && panel === item.id) ||
    panel === 'profile' ||
    panel === 'shortcuts' ||
    panel === 'auth';

  return (
    <header
      className={`pointer-events-none absolute inset-x-0 top-0 z-[45] flex items-start gap-1.5 p-1.5 pt-[max(0.35rem,env(safe-area-inset-top))] sm:gap-3 sm:p-4 ${
        spotlighting ? 'z-[50]' : ''
      }`}
    >
      {user && (
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
          <div className="pointer-events-auto shrink-0">
            <NotificationsBell />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <ResourceHud />
          </div>
        </div>
      )}
      <div
        ref={menuRef}
        className="pointer-events-auto relative ml-auto flex max-w-full shrink-0 flex-nowrap items-center justify-end gap-1 sm:gap-2"
      >
        {wallet && (
          <div
            className={`shrink-0 rounded-lg border border-[var(--line)] bg-[var(--bg-panel)] px-1.5 py-1 backdrop-blur-md sm:rounded-2xl sm:px-3 sm:py-2 ${
              wallet.charges > 0 || spotCharges ? 'animate-charge' : ''
            } ${spotCharges ? spotClass : ''} ${spotNav ? 'invisible' : ''}`}
            title={t('panels:topBar.chargesTitle')}
          >
            <div className="hidden text-[10px] uppercase tracking-wider text-[var(--muted)] sm:block">
              {t('panels:topBar.charges')}
            </div>
            <div className="text-[10px] font-semibold tabular-nums sm:text-sm">
              {wallet.charges}/{wallet.max}
              <span className="ml-1 text-[9px] font-normal text-[var(--muted)] sm:ml-2 sm:text-xs">
                {formatCountdown(remaining, t('panels:topBar.full'))}
              </span>
            </div>
          </div>
        )}

        {/* Grand écran: boutons en ligne (évite le débordement tablette) */}
        <div
          className={`hidden items-center gap-2 lg:flex ${spotCharges ? 'invisible' : ''}`}
        >
          {navItems.map(
            (item) =>
              item.show && (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectNav(item.id)}
                  className={`${panelBtnClass(panel === item.id)} relative ${spotNav ? spotClass : ''} ${
                    item.id === 'empire' && zones?.overCap
                      ? 'animate-pulse ring-2 ring-[var(--danger)]/80 shadow-[0_0_12px_rgba(220,60,60,0.55)]'
                      : ''
                  }`}
                  title={
                    item.id === 'empire' && zones
                      ? t('panels:topBar.zonesTitle', { count: zones.count, max: zones.max })
                      : undefined
                  }
                >
                  {item.id === 'empire' && zones ? (
                    <span className="inline-flex items-baseline gap-1">
                      {t('common:nav.empire')}
                      <span
                        className={`tabular-nums text-[10px] font-bold ${
                          zones.overCap ? 'text-[var(--danger)]' : 'text-[var(--muted)]'
                        }`}
                      >
                        {zones.count}/{zones.max}
                      </span>
                    </span>
                  ) : (
                    item.label
                  )}
                  {item.dot && (
                    <span
                      aria-label={t('panels:topBar.unreadMessages')}
                      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[var(--danger)] ring-2 ring-[var(--bg-panel)]"
                    />
                  )}
                  {!item.dot && item.badge != null && (
                    <span className="absolute -right-1.5 -top-1.5 min-w-4 rounded-full bg-[var(--danger)] px-1 text-[9px] font-bold text-white">
                      {item.badge}
                    </span>
                  )}
                </button>
              ),
          )}

          {user ? (
            <button
              type="button"
              onClick={() => selectNav('profile')}
              className={`flex max-w-none items-center gap-2 ${panelBtnClass(panel === 'profile')} ${spotNav ? spotClass : ''}`}
            >
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-full ring-1 ring-white/30"
                style={{ background: user.empireColor }}
              />
              <span className="truncate">{user.username}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={openAuth}
              className={`shrink-0 rounded-2xl bg-[var(--accent)] px-3 py-2 text-xs font-semibold text-[#1a1408] ${spotlighting ? 'invisible' : ''}`}
            >
              {t('common:nav.login')}
            </button>
          )}
        </div>

        {/* Mobile / tablette: menu déroulant avec toutes les entrées */}
        <div className={`relative lg:hidden ${spotCharges ? 'invisible' : ''}`}>
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label={t('panels:topBar.menu')}
            onClick={() => setMenuOpen((o) => !o)}
            className={`relative flex items-center gap-1.5 ${panelBtnClass(menuActive)} ${spotNav ? spotClass : ''}`}
          >
            <span className="flex w-3.5 flex-col gap-0.5" aria-hidden>
              <span className="h-0.5 w-full rounded-full bg-current" />
              <span className="h-0.5 w-full rounded-full bg-current" />
              <span className="h-0.5 w-full rounded-full bg-current" />
            </span>
            {t('panels:topBar.menu')}
            {totalBadge > 0 && (
              <span className="absolute -right-1.5 -top-1.5 min-w-4 rounded-full bg-[var(--danger)] px-1 text-[9px] font-bold text-white">
                {totalBadge}
              </span>
            )}
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-30 mt-1.5 w-[min(16rem,calc(100vw-1.25rem))] overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--bg-panel)] py-1 shadow-xl backdrop-blur-xl"
            >
              {navItems.map(
                (item) =>
                  item.show && (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      onClick={() => selectNav(item.id)}
                      className={`relative flex w-full items-center justify-between px-3.5 py-3 text-left text-sm font-medium transition ${
                        panel === item.id
                          ? 'bg-[var(--accent)]/15 text-[var(--accent)]'
                          : 'text-[var(--fg)] hover:bg-white/5'
                      } ${spotNav ? spotClass : ''} ${
                        item.id === 'empire' && zones?.overCap ? 'bg-[var(--danger)]/10 text-[var(--danger)]' : ''
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {item.label}
                        {item.id === 'empire' && zones && (
                          <span
                            className={`tabular-nums text-xs font-bold ${
                              zones.overCap ? 'text-[var(--danger)]' : 'text-[var(--muted)]'
                            }`}
                          >
                            {zones.count}/{zones.max}
                          </span>
                        )}
                        {item.dot && (
                          <span
                            aria-label={t('panels:topBar.unreadMessages')}
                            className="h-2 w-2 rounded-full bg-[var(--danger)]"
                          />
                        )}
                      </span>
                      {!item.dot && item.badge != null && (
                        <span className="ml-2 min-w-4 rounded-full bg-[var(--danger)] px-1 text-center text-[9px] font-bold text-white">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  ),
              )}

              <div className="my-1 border-t border-[var(--line)]" />

              {user ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => selectNav('profile')}
                  className={`flex w-full items-center gap-2 px-3.5 py-3 text-left text-sm font-medium transition ${
                    panel === 'profile' || panel === 'shortcuts'
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)]'
                      : 'text-[var(--fg)] hover:bg-white/5'
                  } ${spotNav ? spotClass : ''}`}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-white/30"
                    style={{ background: user.empireColor }}
                  />
                  <span className="truncate">{user.username}</span>
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={openAuth}
                  className="flex w-full items-center px-3.5 py-3 text-left text-sm font-semibold text-[var(--accent)] hover:bg-white/5"
                >
                  {t('common:nav.login')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
